import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  AnnotationMetadata,
  createAnnotationEngine,
  HTMLAnchorFragment,
  IAnnotationEngine,
  setupShadowDomSelectionHandler,
} from '@/features/annotation'
import {
  useAnchorPosition,
  useAnnotationHighlights,
  useStableSelection,
} from '@/features/annotation/hooks'
import { DomLocatorService } from '@/features/annotation/utils'
import { useDocumentStore } from '@/features/document'
import { ResponseThread } from '@/schema'

import {
  asHtmlAnnotation,
  useGetThreadsList,
} from '../../annotation-management'
import { useCommentStore, useTflViewerStore } from '../store'
import { PendingAnnotationState } from '../types'

import { useDocumentHtml } from './useDocumentViewer'
import { useMarkHighlight } from './useMarkHighlight'

interface UseHtmlViewerControllerParams {
  documentId: string
}

export interface UseHtmlViewerControllerResult {
  htmlById: string | undefined
  containerRef: React.RefObject<HTMLDivElement>
  shadowHostRef: React.RefObject<HTMLDivElement>
  shadowRootRef: React.RefObject<ShadowRoot | null>
  pendingBubble: {
    show: boolean
    position: { top: number; left: number } | null
    onAddComment: () => void
  }
  dialogState: {
    open: boolean
    documentVersionId: string | undefined
    annotation: AnnotationMetadata | null
    onOpenChange: (open: boolean) => void
    onSuccess: (commentId: string) => void
  }
  avatars: Array<{
    commentId: string
    comment: ResponseThread
    fragment: HTMLAnchorFragment
    isActive: boolean
    engine: IAnnotationEngine
  }>
  showCommentAvatars: boolean
  setShowCommentAvatars: (show: boolean) => void
  onAvatarClick: (commentId: string) => void
  onClickOutside: (event: React.MouseEvent<HTMLDivElement>) => void
  bubbleOffset: { top: number; left: number }
}

export const useHtmlViewerController = ({
  documentId,
}: UseHtmlViewerControllerParams): UseHtmlViewerControllerResult => {
  const { metadataByDocId, setRightSidebarCollapsed } = useTflViewerStore()
  const { activeDocumentId, getIsLatestVersion } = useDocumentStore()
  const isLatestVersion = activeDocumentId
    ? getIsLatestVersion(activeDocumentId)
    : true
  const htmlById = useDocumentHtml(documentId)
  const shadowHostRef = useRef<HTMLDivElement>(null)
  const shadowRootRef = useRef<ShadowRoot | null>(null)
  const [engine, setEngine] = useState<IAnnotationEngine | null>(null)
  const domLocatorRef = useRef<DomLocatorService | null>(null)

  const [pendingAnnotation, setPendingAnnotation] =
    useState<PendingAnnotationState>({
      metadata: null,
      showBubble: false,
      bubblePosition: null,
    })
  const [dialogOpen, setDialogOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const documentVersionId = metadataByDocId[documentId]?.version_id
  const isActive = useMemo(
    () => activeDocumentId === documentId,
    [activeDocumentId, documentId]
  )

  useMarkHighlight({
    isReady: !!engine,
    containerRef: shadowRootRef,
    documentId: documentId,
    dependencies: [htmlById],
  })

  const {
    setComments,
    setAnnotation,
    activeCommentId,
    setActiveCommentId,
    setActiveDocumentVersionId,
    commentsByDocVersion,
    annotationsByDocVersion,
    showCommentAvatarsByDoc,
    setShowCommentAvatars,
  } = useCommentStore()

  const showCommentAvatars = showCommentAvatarsByDoc[documentId] ?? true

  const { data } = useGetThreadsList({
    documentVersionId: documentVersionId ?? '',
  })

  const comments = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data) || null
  }, [data])

  const bubbleFragment = useMemo(() => {
    if (!pendingAnnotation.metadata) return null
    const html = asHtmlAnnotation(pendingAnnotation.metadata)
    if (!html) return null
    return html.fragments[0] || null
  }, [pendingAnnotation.metadata])

  const bubbleOffset = useMemo(() => ({ top: -45, left: -12 }), [])

  const { position: bubblePosition, recalculate: recalculateBubblePosition } =
    useAnchorPosition({
      fragment: bubbleFragment,
      containerRef: containerRef,
      shadowRootRef: shadowRootRef,
      engine: engine,
      enabled: !!bubbleFragment && !!engine,
      offset: bubbleOffset,
    })

  useEffect(() => {
    if (bubbleFragment && domLocatorRef.current && engine) {
      recalculateBubblePosition()
    }
  }, [engine, bubbleFragment, recalculateBubblePosition])

  useEffect(() => {
    setPendingAnnotation({
      metadata: null,
      showBubble: false,
      bubblePosition: null,
    })
    setDialogOpen(false)
  }, [documentId, htmlById])

  const clearShadowSelection = useCallback(() => {
    const shadowRoot = shadowRootRef.current
    if (!shadowRoot) return

    const localSelection = (
      shadowRoot as unknown as { getSelection?: () => Selection }
    ).getSelection?.()
    const selection =
      localSelection && localSelection.rangeCount > 0
        ? localSelection
        : globalThis.getSelection()

    if (selection && selection.rangeCount > 0) {
      selection.removeAllRanges()
    }

    const event = new CustomEvent('annotation-selection-cleared', {
      bubbles: true,
      composed: true,
    })
    shadowRoot.host.dispatchEvent(event)
  }, [])

  const handleStableSelection = useCallback(
    (metadata: AnnotationMetadata | null) => {
      if (!metadata || !shadowRootRef.current) {
        setPendingAnnotation({
          metadata: null,
          showBubble: false,
          bubblePosition: null,
        })
        return
      }

      if (metadata.fragments.length > 0) {
        setPendingAnnotation({
          metadata,
          showBubble: true,
          bubblePosition: null,
        })
      }
    },
    []
  )

  const handleAddComment = useCallback(() => {
    setDialogOpen(true)
  }, [])

  const handleCommentSuccess = useCallback(
    (commentId: string) => {
      if (!pendingAnnotation.metadata || !engine || !documentVersionId) return

      const html = asHtmlAnnotation(pendingAnnotation.metadata)
      if (!html) return

      setAnnotation(documentVersionId, commentId, html)
      setActiveCommentId(commentId)

      setPendingAnnotation({
        metadata: null,
        showBubble: false,
        bubblePosition: null,
      })
    },
    [
      pendingAnnotation.metadata,
      engine,
      setAnnotation,
      setActiveCommentId,
      documentVersionId,
    ]
  )

  const handleDialogClose = useCallback(
    (open: boolean) => {
      setDialogOpen(open)
      if (!open) {
        setPendingAnnotation({
          metadata: null,
          showBubble: false,
          bubblePosition: null,
        })
        clearShadowSelection()
      }
    },
    [clearShadowSelection]
  )

  const handleAvatarClick = useCallback(
    (commentId: string) => {
      setActiveCommentId(commentId)
      setRightSidebarCollapsed(false)
    },
    [setActiveCommentId, setRightSidebarCollapsed]
  )

  const handleClickOutside = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (dialogOpen) {
        return
      }

      const target = event.target as HTMLElement
      const composedPath = event.nativeEvent.composedPath()

      const isClickOnAvatar = composedPath.some(
        (el) =>
          el instanceof Element && el.hasAttribute?.('data-comment-avatar')
      )
      const isClickOnSelectionBubble = composedPath.some(
        (el) => el instanceof Element && el.hasAttribute?.('data-comment-add')
      )
      const isClickOnHighlight = composedPath.some(
        (el) =>
          el instanceof Element &&
          el.classList?.contains('annotation-highlight')
      )
      const isClickOnSidebar = target.closest('[data-sidebar]') !== null

      if (
        !isClickOnAvatar &&
        !isClickOnHighlight &&
        !isClickOnSidebar &&
        !isClickOnSelectionBubble
      ) {
        setActiveCommentId(undefined)
        setPendingAnnotation({
          metadata: null,
          showBubble: false,
          bubblePosition: null,
        })
      }
    },
    [setActiveCommentId, dialogOpen]
  )

  useStableSelection({
    enabled: !!shadowRootRef.current && !!engine,
    delay: 400,
    shadowRoot: shadowRootRef.current,
    onStableSelection: handleStableSelection,
  })

  useEffect(() => {
    if (!shadowRootRef.current) return

    const shadowHost = shadowRootRef.current.host

    const handleSelectionCleared = () => {
      setPendingAnnotation({
        metadata: null,
        showBubble: false,
        bubblePosition: null,
      })
    }

    shadowHost.addEventListener(
      'annotation-selection-cleared',
      handleSelectionCleared
    )

    return () => {
      shadowHost.removeEventListener(
        'annotation-selection-cleared',
        handleSelectionCleared
      )
    }
  }, [documentId])

  useEffect(() => {
    setActiveDocumentVersionId(documentVersionId)
  }, [documentVersionId, setActiveDocumentVersionId])

  useEffect(() => {
    if (!comments || !documentVersionId || !isActive) return

    setComments(documentVersionId, comments)

    comments.forEach((comment) => {
      if (comment.annotation && comment.annotation?.fragments?.length > 0) {
        const html = asHtmlAnnotation(comment.annotation)
        if (html) {
          setAnnotation(documentVersionId, comment.id, html)
        }
      }
    })
  }, [comments, setComments, setAnnotation, documentVersionId, isActive])

  useEffect(() => {
    if (!isActive) {
      setPendingAnnotation({
        metadata: null,
        showBubble: false,
        bubblePosition: null,
      })
      clearShadowSelection()
    }
  }, [isActive, documentId, clearShadowSelection])

  useEffect(() => {
    const host = shadowHostRef.current
    if (!host) {
      return
    }

    let shadowRoot = shadowRootRef.current
    if (shadowRoot?.host !== host) {
      shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
      shadowRootRef.current = shadowRoot
    }
    if (!shadowRoot) {
      return
    }

    const style = document.createElement('style')
    style.textContent = `
      :host {
        all: initial;
        display: block;
        width: 100%;
      }
      :host, :host * {
        box-sizing: border-box;
      }
      .annotation-highlight {
        background-color: #fef08a;
        cursor: pointer;
        transition: background-color 0.2s;
      }
      .annotation-highlight:hover {
        background-color: #fde047;
      }

      mark {
        background-color: #FFF5CC;
      }
    `

    const content = document.createElement('div')
    content.className = 'content-wrapper'
    content.innerHTML = htmlById ?? ''

    shadowRoot.replaceChildren(style, content)

    const contentWrapper = shadowRoot.querySelector(
      '.content-wrapper'
    ) as HTMLElement
    if (!contentWrapper) {
      return
    }

    const engineInstance = createAnnotationEngine(contentWrapper)
    setEngine(engineInstance)

    domLocatorRef.current = engineInstance.getDomLocator()

    const cleanupHandler = setupShadowDomSelectionHandler(
      shadowRoot,
      engineInstance
    )

    return () => {
      cleanupHandler()
      setEngine(null)
      domLocatorRef.current = null
    }
  }, [htmlById])

  useAnnotationHighlights({
    documentVersionId,
    enabled: !!documentVersionId && !!engine,
    engine,
  })

  const avatars = useMemo(() => {
    if (!documentVersionId || !engine || !showCommentAvatars) return []

    const commentsById = commentsByDocVersion[documentVersionId] ?? {}
    const annotationsByCommentId =
      annotationsByDocVersion[documentVersionId] ?? {}

    return Object.entries(commentsById)
      .map(([commentId, comment]) => {
        const annotation = annotationsByCommentId[commentId]
        if (!annotation) return null

        const html = asHtmlAnnotation(annotation)
        if (!html) return null

        const fragment = html.fragments[0]
        if (!fragment) return null

        return {
          commentId,
          comment,
          fragment,
          isActive: activeCommentId === commentId,
          engine: engine,
        }
      })
      .filter((avatar): avatar is NonNullable<typeof avatar> => avatar !== null)
  }, [
    commentsByDocVersion,
    annotationsByDocVersion,
    activeCommentId,
    documentVersionId,
    engine,
    showCommentAvatars,
  ])

  return {
    htmlById,
    containerRef,
    shadowHostRef,
    shadowRootRef,
    pendingBubble: {
      show: pendingAnnotation.showBubble && !!bubblePosition && isLatestVersion,
      position: bubblePosition,
      onAddComment: handleAddComment,
    },
    dialogState: {
      open: dialogOpen,
      documentVersionId,
      annotation: pendingAnnotation.metadata,
      onOpenChange: handleDialogClose,
      onSuccess: handleCommentSuccess,
    },
    avatars: isActive ? avatars : [],
    showCommentAvatars,
    setShowCommentAvatars: (show: boolean) =>
      setShowCommentAvatars(documentId, show),
    onAvatarClick: handleAvatarClick,
    onClickOutside: handleClickOutside,
    bubbleOffset,
  }
}
